import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '@/components/ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { RetailerEvent } from '@shared/schema';

const eventSchema = z.object({
    name: z.string().min(1, 'Name is required').trim(),
    description: z.string().optional().default(''),
    isActive: z.boolean().default(true),
});

type EventFormValues = z.infer<typeof eventSchema>;

interface EventDialogProps {
    retailerId: number;
    event?: RetailerEvent | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function EventDialog({ retailerId, event, open, onOpenChange }: EventDialogProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const isEditing = !!event;

    const form = useForm<EventFormValues>({
        resolver: zodResolver(eventSchema),
        defaultValues: {
            name: '',
            description: '',
            isActive: true,
        },
    });

    useEffect(() => {
        if (event && open) {
            form.reset({
                name: event.name,
                description: event.description || '',
                isActive: event.isActive,
            });
        } else if (!event && open) {
            form.reset({
                name: '',
                description: '',
                isActive: true,
            });
        }
    }, [event, open, form]);

    const mutation = useMutation({
        mutationFn: async (values: EventFormValues) => {
            if (isEditing && event) {
                return await apiRequest(`/api/retailer-events/${event.id}`, 'PUT', values);
            } else {
                return await apiRequest(`/api/retailers/${retailerId}/events`, 'POST', values);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['retailer-events', retailerId] });
            toast({
                title: 'Success',
                description: `Event ${isEditing ? 'updated' : 'created'} successfully`,
            });
            onOpenChange(false);
        },
        onError: (error: any) => {
            toast({
                title: 'Error',
                description: error.message || `Failed to ${isEditing ? 'update' : 'create'} event`,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = (values: EventFormValues) => {
        mutation.mutate(values);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{isEditing ? 'Edit Event' : 'Create Retailer Event'}</DialogTitle>
                    <DialogDescription>
                        {isEditing
                            ? 'Update the details for this retailer-scoped event.'
                            : 'Add a new reusable event for this retailer (e.g. Easter, Back to School).'}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Event Name</FormLabel>
                                    <FormControl>
                                        <Input placeholder="e.g. Summer Sale 2024" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Description (Optional)</FormLabel>
                                    <FormControl>
                                        <Textarea
                                            placeholder="Shared commercial context for this event..."
                                            className="resize-none"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {isEditing && (
                            <FormField
                                control={form.control}
                                name="isActive"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                                        <FormControl>
                                            <Checkbox
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        </FormControl>
                                        <div className="space-y-1 leading-none">
                                            <FormLabel>
                                                Active
                                            </FormLabel>
                                            <p className="text-sm text-muted-foreground">
                                                Inactive events are hidden from dropdowns.
                                            </p>
                                        </div>
                                    </FormItem>
                                )}
                            />
                        )}

                        <DialogFooter className="pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={mutation.isPending}>
                                {mutation.isPending ? 'Saving...' : 'Save Event'}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
